import { useCallback, useMemo, useState } from "react";

export interface UseWizardOptions {
  /** Cuántos pasos tiene el wizard. */
  total: number;
  /** Paso inicial, base 0. Por defecto el primero. */
  initial?: number;
  /**
   * Si se puede salir del paso `from`. Devuelve `false` (o una promesa que
   * resuelva a `false`) para quedarse donde está.
   *
   * Vive FUERA del hook a propósito: quién puede avanzar depende de qué se
   * está llenando —en un formulario, de validar los campos de ese paso— y
   * eso lo sabe la pantalla, no el contador de pasos. Al hook le toca el
   * movimiento; a la pantalla, el permiso.
   */
  canAdvance?: (from: number) => boolean | Promise<boolean>;
}

export interface Wizard {
  /** Paso actual, base 0. */
  step: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  /**
   * Intenta avanzar un paso. Resuelve a `true` si se movió.
   *
   * Pregunta a `canAdvance` antes de moverse; en el último paso no hace
   * nada, porque de ahí no se avanza sino que se envía.
   */
  next: () => Promise<boolean>;
  /** Retrocede un paso. Nunca pregunta: volver a corregir no se condiciona. */
  back: () => void;
  /**
   * Salta a un paso concreto, sin preguntar. Es lo que usan el "Editar" del
   * resumen y el reparto de un error del servidor a su paso.
   */
  goTo: (step: number) => void;
}

/**
 * El estado de un wizard: en qué paso va, cómo se avanza y cómo se vuelve.
 *
 * Vive aparte de la pantalla porque el registro no va a ser el único: la
 * activación y el checkout se recorren igual, y el paso actual, el "no
 * avanzar si esto no valida" y el salto a un paso concreto son las mismas
 * tres decisiones en los tres sitios.
 *
 * Lo que NO hace, también a propósito: no guarda los valores del
 * formulario. Un solo `useForm` para todo el wizard ya conserva lo
 * capturado al ir y volver; duplicarlo aquí sería una segunda copia que se
 * desincroniza.
 */
export function useWizard({ total, initial = 0, canAdvance }: UseWizardOptions): Wizard {
  const [step, setStep] = useState(initial);

  // Un paso fuera de rango no debe poder existir: el resumen ofrece saltos
  // por índice y un `goTo(5)` en un wizard de tres dejaría la card vacía.
  const clamp = useCallback(
    (value: number) => Math.min(Math.max(value, 0), total - 1),
    [total],
  );

  const next = useCallback(async () => {
    if (step >= total - 1) return false;
    if (canAdvance && !(await canAdvance(step))) return false;
    setStep((current) => clamp(current + 1));
    return true;
  }, [canAdvance, clamp, step, total]);

  const back = useCallback(() => setStep((current) => clamp(current - 1)), [clamp]);

  const goTo = useCallback((value: number) => setStep(clamp(value)), [clamp]);

  return useMemo(
    () => ({
      step,
      total,
      isFirst: step === 0,
      isLast: step === total - 1,
      next,
      back,
      goTo,
    }),
    [back, goTo, next, step, total],
  );
}
